// Sprint 20.5 PR-2 (D013-EC-68) — runner-side SESSION_LOST banner.
//
// Rendered above the composer in `ChatView` when the runner emits
// `agent.error({code: 'SESSION_LOST'})` because it could not respawn
// the Claude child to forward a queued user_message. The agent's
// status has already been flipped to `error` by the SessionManager;
// this banner is the founder-mode recovery affordance on top of that.
//
// Affordances:
//   • Retry → clears the local latch so the composer becomes usable.
//     The user's next message goes through the normal send path, which
//     fires another runner-side respawn attempt (with `--resume <session_id>`
//     if one was captured before the prior child exited). The retry
//     does NOT call a backend endpoint of its own — the runner's
//     respawn-on-user_message path is the recovery primitive.
//   • Dismiss (×) → silent local close. Re-renders if the runner emits
//     another SESSION_LOST.
//
// Founder voice: "Esta sesión terminó. Pulsa retry para reanudarla." —
// no jargon, no stack trace, no "Process exited with code 1". Copy
// lives in the i18n bundle so the wording can move without touching
// this component.

import { useTranslation } from '../../i18n';
import { useAppStore } from '../../stores/app.store';

export interface SessionLostBannerProps {
  agentId: string;
  message: string | null;
}

export function SessionLostBanner({ agentId, message }: SessionLostBannerProps) {
  const { t } = useTranslation();
  const clearSessionLost = useAppStore((s) => s.clearSessionLost);

  const handleRetry = (): void => {
    clearSessionLost(agentId);
  };
  const handleDismiss = (): void => {
    clearSessionLost(agentId);
  };

  return (
    <div
      role="alert"
      aria-live="polite"
      data-testid="session-lost-banner"
      data-agent-id={agentId}
      className="mx-3 mt-2 mb-2 flex items-start justify-between gap-3 rounded-md border border-status-error/30 bg-status-error/10 px-3 py-2 text-body text-foreground"
    >
      <div className="flex flex-col gap-1 min-w-0">
        <span className="text-status-error font-medium">
          {t('sessionLostBanner.title')}
        </span>
        <span className="text-foreground/80 text-caption">
          {message ? message : t('sessionLostBanner.subtitle')}
        </span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={handleRetry}
          data-testid="session-lost-retry-btn"
          className="px-3 py-1 rounded-md bg-status-error/20 text-status-error hover:bg-status-error/30 disabled:opacity-60 text-caption font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-status-error/40"
        >
          {t('sessionLostBanner.retry')}
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label={t('sessionLostBanner.dismissAria')}
          data-testid="session-lost-dismiss"
          className="text-foreground/60 hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-status-error/40 rounded-sm px-1"
        >
          {t('sessionLostBanner.dismissGlyph')}
        </button>
      </div>
    </div>
  );
}
